import React, { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  AUTH_MODE,
  isFirebaseAuthMode,
  sendOtpFirebase,
  verifyOtpFirebase,
  resetFirebasePhoneAuth,
} from "@/lib/firebaseClient";
import { toast } from "sonner";
import { Phone, ArrowRight, ShieldCheck } from "@phosphor-icons/react";
import QLogo from "@/components/QLogo";

export default function PhoneLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");
  const refs = useRef([]);
  const firebaseMode = isFirebaseAuthMode();

  const fullPhone = phone.startsWith("+") ? phone : `+91${phone}`;

  const sendOtp = async () => {
    if (phone.replace(/\D/g, "").length < 8) {
      toast.error("Enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      if (firebaseMode) {
        await sendOtpFirebase(fullPhone);
        setHint("");
      } else {
        const { data } = await api.post("/auth/send-otp", { phone: fullPhone });
        if (data.dev_otp_hint) setHint(data.dev_otp_hint);
      }
      toast.success("OTP sent");
      setStep(2);
      setTimeout(() => refs.current[0]?.focus(), 80);
    } catch (e) {
      // Surface the actual error so we can diagnose Firebase reCAPTCHA / domain / quota issues.
      const code = e?.code || "";
      const fbMsg = e?.message || "";
      const apiMsg = formatApiErrorDetail(e.response?.data?.detail);
      console.error("[qd-otp] send failed", { code, message: fbMsg, error: e });
      const display = apiMsg || (code ? `${code}` : "") || fbMsg || "Failed to send OTP";
      toast.error(display);
    } finally { setLoading(false); }
  };

  const handleOtpChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 5) refs.current[idx + 1]?.focus();
  };

  const handleKey = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) refs.current[idx - 1]?.focus();
    if (e.key === "Enter") verify();
  };

  const verify = async () => {
    const code = otp.join("");
    if (code.length !== 6) { toast.error("Enter 6-digit OTP"); return; }
    setLoading(true);
    try {
      let token, user;
      if (firebaseMode) {
        const { idToken } = await verifyOtpFirebase(code);
        const { data } = await api.post("/auth/firebase", { id_token: idToken });
        ({ token, user } = data);
      } else {
        const { data } = await api.post("/auth/verify-otp", { phone: fullPhone, otp: code });
        ({ token, user } = data);
      }
      login(token, user);
      toast.success("Welcome to Q Drives");
      nav(user?.role === "admin" ? "/admin" : "/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" data-testid="login-page">
      {/* BG */}
      <div className="absolute inset-0 -z-10">
        <img
          src="/hero-bg.png"
          alt=""
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/70 to-black" />
      </div>

      <header className="relative px-6 pb-5 qd-pt-safe-header">
        <QLogo size={28} />
      </header>

      {/* Invisible reCAPTCHA host — Firebase Phone Auth requires this div */}
      <div id="qd-recaptcha" />

      <main className="flex-1 flex flex-col justify-center px-6 max-w-md mx-auto w-full pb-24">
        <div className="mb-10 flex justify-center">
          <QLogo variant="full" size={210} />
        </div>
        <h1 className="qd-h1 text-3xl text-white mb-3 text-center" style={{ letterSpacing: "-0.02em" }}>
          Welcome to Q Drives
        </h1>
        <p className="text-sm text-white/55 mb-10 max-w-sm text-center mx-auto">
          Phone verification for secure access to Q Drives.
        </p>

        {step === 1 && (
          <div data-testid="phone-step">
            <label className="qd-label">Mobile Number</label>
            <div className="flex items-center gap-3 mb-7">
              <div className="text-base text-white/55 pb-[12px] border-b-[1.5px] border-white/22">+91</div>
              <input
                data-testid="phone-input"
                className="qd-input flex-1"
                placeholder="98765 43210"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => { if (e.key === "Enter") sendOtp(); }}
              />
            </div>
            <button
              data-testid="send-otp-btn"
              className="btn-primary w-full flex items-center justify-center gap-2"
              onClick={sendOtp}
              disabled={loading}
            >
              {loading ? "Sending…" : "Send OTP"} <ArrowRight size={15} weight="bold" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="otp-step">
            <label className="qd-label">Enter 6-Digit OTP</label>
            <div className="flex gap-2 mb-4">
              {otp.map((v, i) => (
                <input
                  key={i}
                  ref={(el) => (refs.current[i] = el)}
                  data-testid={`otp-input-${i}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={v}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleKey(i, e)}
                  className="qd-input text-center !text-xl"
                  style={{ width: 42 }}
                />
              ))}
            </div>
            {hint && process.env.NODE_ENV !== "production" && AUTH_MODE !== "firebase" && (
              <div className="text-[11px] text-white/40 font-mono-data mb-6 tracking-wider">
                DEV MODE · OTP: <span className="text-[#FF1E1E] font-bold">{hint}</span>
              </div>
            )}
            <button
              data-testid="verify-otp-btn"
              className="btn-primary w-full mb-3"
              onClick={verify}
              disabled={loading}
            >
              {loading ? "Verifying…" : "Verify & Continue"}
            </button>
            <button
              data-testid="resend-otp-btn"
              className="btn-ghost w-full"
              onClick={() => { resetFirebasePhoneAuth(); setStep(1); setOtp(["","","","","",""]); }}
            >
              Change number
            </button>
          </div>
        )}
      </main>
      <footer
        data-testid="site-footer"
        className="mt-8 pt-4 border-t border-[#1A1A1A] flex items-center justify-between gap-3 text-[10px] font-mono-data tracking-widest uppercase text-white/45 px-4 pb-6"
      >
        <div>© Q Drives Automotive Private Limited</div>
        <nav className="flex items-center gap-4">
          <Link
            to="/privacy-policy"
            data-testid="site-footer-privacy-link"
            className="text-white/60 hover:text-white underline decoration-white/20 hover:decoration-white"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            data-testid="site-footer-terms-link"
            className="text-white/60 hover:text-white underline decoration-white/20 hover:decoration-white"
          >
            Terms
          </Link>
        </nav>
      </footer>
    </div>
  );
}
